import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { changePassword } from '../../redux/settingsSlice';
import ToastService from '../../core/notifications/ToastService';

export const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.settings);
  
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [validationError, setValidationError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setValidationError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      setValidationError('New passwords do not match');
      return;
    }

    if (formData.new_password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }

    try {
      await dispatch(changePassword({
        current_password: formData.current_password,
        new_password: formData.new_password
      })).unwrap();
      
      ToastService.success('Password changed successfully');
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err: any) {
      ToastService.error(err || 'Failed to change password');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton
          onClick={() => navigate('/settings')}
          sx={{ display: { xs: 'flex', sm: 'none' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/settings')}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Security Settings
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
            Manage your password and security preferences
          </Typography>
        </Box>
      </Box>

      {(error || validationError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || validationError}
        </Alert>
      )}

      {/* Change Password */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <LockIcon sx={{ color: '#14B8A6' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Change Password
            </Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  name="current_password"
                  value={formData.current_password}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleChange}
                  required
                  helperText="Must be at least 8 characters"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button
                    onClick={() => navigate('/settings')}
                    disabled={isLoading}
                    fullWidth={window.innerWidth < 600}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{ backgroundColor: '#14B8A6' }}
                    fullWidth={window.innerWidth < 600}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Change Password'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SecurityPage;
